import {
  ARCHIVO_BOLD_WIDTHS,
  ARCHIVO_REGULAR_WIDTHS,
  FALLBACK_GLYPH_WIDTH,
} from './archivoGlyphWidths';

export type PdfFont = {
  size: number;
  bold: boolean;
};

/** `defaultTextFontParams.size` of the PDF service. */
export const PDF_DEFAULT_FONT_SIZE = 9;

const LINE_HEIGHT_FACTOR = 1.15;
const GLYPH_UNITS_PER_EM = 1000;

export const regularFont = (size = PDF_DEFAULT_FONT_SIZE): PdfFont => ({
  size,
  bold: false,
});

export const boldFont = (size = PDF_DEFAULT_FONT_SIZE): PdfFont => ({
  size,
  bold: true,
});

export function pdfLineHeight(font: PdfFont): number {
  return font.size * LINE_HEIGHT_FACTOR;
}

function glyphWidths(font: PdfFont, text: string): number[] {
  const widths = font.bold ? ARCHIVO_BOLD_WIDTHS : ARCHIVO_REGULAR_WIDTHS;
  return [...text].map(
    (glyph) =>
      ((widths.get(glyph) ?? FALLBACK_GLYPH_WIDTH) * font.size) / GLYPH_UNITS_PER_EM
  );
}

const sum = (values: number[]): number =>
  values.reduce((total, value) => total + value, 0);

export function measurePdfTextWidth(text: string, font: PdfFont): number {
  return sum(glyphWidths(font, text));
}

/** jsPDF `splitLongWord`: fills the hanging line first, then whole lines. */
function splitLongWord(
  glyphs: string[],
  widths: number[],
  firstLineMaxWidth: number,
  maxWidth: number
): string[] {
  const chunks: string[] = [];
  let index = 0;
  let workingWidth = 0;
  while (index < glyphs.length && workingWidth + widths[index] < firstLineMaxWidth) {
    workingWidth += widths[index];
    index += 1;
  }
  chunks.push(glyphs.slice(0, index).join(''));

  let lineStart = index;
  workingWidth = 0;
  for (; index < glyphs.length; index += 1) {
    if (workingWidth + widths[index] > maxWidth) {
      chunks.push(glyphs.slice(lineStart, index).join(''));
      workingWidth = 0;
      lineStart = index;
    }
    workingWidth += widths[index];
  }
  if (lineStart !== glyphs.length) {
    chunks.push(glyphs.slice(lineStart).join(''));
  }
  return chunks;
}

/** jsPDF `splitParagraphIntoLines` (no indent options). */
function wrapParagraph(paragraph: string, maxWidth: number, font: PdfFont): string[] {
  const spaceWidth = measurePdfTextWidth(' ', font);
  let line: string[] = [];
  const lines: string[][] = [line];
  let lineWidth = 0;
  let separatorWidth = 0;

  for (const word of paragraph.split(' ')) {
    const glyphs = [...word];
    const widths = glyphWidths(font, word);
    let wordWidth = sum(widths);

    if (lineWidth + separatorWidth + wordWidth > maxWidth) {
      if (wordWidth > maxWidth) {
        const chunks = splitLongWord(
          glyphs,
          widths,
          maxWidth - (lineWidth + separatorWidth),
          maxWidth
        );
        line.push(chunks.shift() ?? '');
        const lastChunk = chunks.pop() ?? '';
        line = [lastChunk];
        chunks.forEach((chunk) => lines.push([chunk]));
        wordWidth = measurePdfTextWidth(lastChunk, font);
      } else {
        line = [word];
      }
      lines.push(line);
      lineWidth = wordWidth;
    } else {
      line.push(word);
      lineWidth += separatorWidth + wordWidth;
    }
    separatorWidth = spaceWidth;
  }

  return lines.map((words) => words.join(' '));
}

/** jsPDF `splitTextToSize`, the line breaking of every plain `text` item. */
export function wrapPdfText(text: string, maxWidth: number, font: PdfFont): string[] {
  return text
    .split(/\r?\n/)
    .flatMap((paragraph) => wrapParagraph(paragraph, maxWidth, font));
}

/** jsPDF `getTextDimensions(text, { maxWidth }).w`: the widest wrapped line. */
function measureWrappedWidth(text: string, maxWidth: number, font: PdfFont): number {
  return Math.max(
    0,
    ...wrapPdfText(text, maxWidth, font).map((line) => measurePdfTextWidth(line, font))
  );
}

type RichTextRun = { text: string; bold: boolean; badge: boolean };
type RichTextToken = RichTextRun | 'lineBreak';

type RichTextStyle = Omit<RichTextRun, 'text'>;

/** Horizontal padding of a communication badge (span with a background), both sides. */
const RICH_TEXT_BADGE_PADDING = 16;
const BOLD_TAGS = new Set(['b', 'strong', 'code']);
const BLOCK_TAGS = new Set(['p', 'div']);
const LIST_TAGS = new Set(['ul', 'ol']);

/** Tokenizer of the PDF service `RichText` (`parseRichTextTokens`). */
class RichTextTokenizer {
  readonly tokens: RichTextToken[] = [];

  constructor(html: string, baseBold: boolean) {
    const body = new DOMParser().parseFromString(
      `<body>${html}</body>`,
      'text/html'
    ).body;
    this.visitChildren(body, { bold: baseBold, badge: false });
    while (this.tokens[0] === 'lineBreak') {
      this.tokens.shift();
    }
    while (this.tokens[this.tokens.length - 1] === 'lineBreak') {
      this.tokens.pop();
    }
  }

  private visitChildren(node: Node, style: RichTextStyle): void {
    node.childNodes.forEach((child) => this.visit(child, style));
  }

  private visit(node: Node, style: RichTextStyle): void {
    if (node.nodeType === Node.TEXT_NODE) {
      this.appendText(node.textContent ?? '', style);
      return;
    }
    if (!(node instanceof Element)) {
      return;
    }
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') {
      this.appendLineBreak();
    } else if (BLOCK_TAGS.has(tag)) {
      this.appendLineBreak();
      this.visitChildren(node, style);
      this.appendLineBreak();
    } else if (LIST_TAGS.has(tag)) {
      this.appendLineBreak();
      this.visitList(node, style, tag === 'ol');
      this.appendLineBreak();
    } else if (tag === 'li') {
      this.visitListItem(node, style, '- ');
    } else if (tag === 'span') {
      const badge = /background/i.test(node.getAttribute('style') ?? '');
      this.visitChildren(node, { ...style, badge: style.badge || badge });
    } else {
      this.visitChildren(node, { ...style, bold: style.bold || BOLD_TAGS.has(tag) });
    }
  }

  private visitList(list: Element, style: RichTextStyle, ordered: boolean): void {
    let itemIndex = 1;
    list.childNodes.forEach((child) => {
      if (child instanceof Element && child.tagName.toLowerCase() === 'li') {
        this.visitListItem(child, style, ordered ? `${itemIndex}. ` : '- ');
        itemIndex += 1;
      } else {
        this.visit(child, style);
      }
    });
  }

  private visitListItem(item: Element, style: RichTextStyle, prefix: string): void {
    this.appendLineBreak();
    this.appendText(prefix, style);
    this.visitChildren(item, style);
    this.appendLineBreak();
  }

  private appendText(text: string, style: RichTextStyle): void {
    const normalized = text.replace(/\s+/g, ' ');
    if (normalized.length > 0) {
      this.tokens.push({ text: normalized, ...style });
    }
  }

  private appendLineBreak(): void {
    const last = this.tokens[this.tokens.length - 1];
    if (last !== undefined && last !== 'lineBreak') {
      this.tokens.push('lineBreak');
    }
  }
}

/** Line layout of the PDF service `RichText` (`calculateRichTextProperties`). */
class RichTextLineCounter {
  private lineCount = 0;
  private lineWidth = 0;
  private lineHasSegments = false;

  constructor(
    private readonly maxWidth: number,
    private readonly size: number
  ) {}

  count(tokens: RichTextToken[]): number {
    tokens.forEach((token) => {
      if (token === 'lineBreak') {
        this.pushLine();
        return;
      }
      (token.text.match(/\S+\s*|\s+/g) ?? []).forEach((part) =>
        this.appendPart(part, token)
      );
    });
    if (this.lineHasSegments || this.lineCount === 0) {
      this.pushLine();
    }
    return this.lineCount;
  }

  private measure(text: string, run: RichTextRun): number {
    const width = measureWrappedWidth(text, this.maxWidth, {
      size: this.size,
      bold: run.bold,
    });
    return run.badge ? width + RICH_TEXT_BADGE_PADDING : width;
  }

  private appendPart(part: string, run: RichTextRun): void {
    let remaining = this.lineWidth === 0 ? part.replace(/^\s+/, '') : part;
    while (remaining.length > 0) {
      const width = this.measure(remaining, run);
      if (this.lineWidth > 0 && this.lineWidth + width > this.maxWidth) {
        this.pushLine();
        remaining = remaining.replace(/^\s+/, '');
        continue;
      }
      if (width <= this.maxWidth || remaining.length === 1) {
        this.appendSegment(width);
        return;
      }
      const splitIndex = this.findSplitIndex(remaining, run);
      this.appendSegment(this.measure(remaining.slice(0, splitIndex), run));
      this.pushLine();
      remaining = remaining.slice(splitIndex).replace(/^\s+/, '');
    }
  }

  private findSplitIndex(text: string, run: RichTextRun): number {
    for (let index = 1; index <= text.length; index += 1) {
      if (this.measure(text.slice(0, index), run) > this.maxWidth) {
        return Math.max(1, index - 1);
      }
    }
    return text.length;
  }

  private appendSegment(width: number): void {
    this.lineWidth += width;
    this.lineHasSegments = true;
  }

  private pushLine(): void {
    this.lineCount += 1;
    this.lineWidth = 0;
    this.lineHasSegments = false;
  }
}

export function countPdfRichTextLines(
  html: string,
  maxWidth: number,
  font: PdfFont
): number {
  const tokens = new RichTextTokenizer(html, font.bold).tokens;
  return new RichTextLineCounter(maxWidth, font.size).count(tokens);
}
